// Client for the Telegram Mini App auth flow (supabase/functions/telegram-auth).
// Used only by the /telegram-app page, which runs inside Telegram's WebView.
import { supabase } from "./supabase";
import { throwUa } from "./errors";
import { FunctionsHttpError } from "@supabase/supabase-js";

export const getFunctionErrorCode = async (
  error: unknown
): Promise<string | null> => {
  if (!(error instanceof FunctionsHttpError)) return null;
  try {
    const body = await error.context.json();
    return typeof body?.error === "string" ? body.error : null;
  } catch {
    return null;
  }
};

export interface TelegramAppStatus {
  telegramId: number;
  linkedUserId: string | null;
  currentUserId: string | null;
}

export const getTelegramAppInitData = (): string | null =>
  window.Telegram?.WebApp?.initData || null;

export const isTelegramAppAuth = (search: string): boolean =>
  new URLSearchParams(search).get("telegram") === "true";

export const fetchTelegramAppStatus = async (
  initData: string
): Promise<TelegramAppStatus> => {
  const { data, error } = await supabase.functions.invoke("telegram-auth", {
    body: { action: "status", initData },
  });
  if (error) throwUa(error);
  return data as TelegramAppStatus;
};

// If this Telegram account is already linked, redeems a session for its
// owner directly (no bot redirect). Returns linked:false when there is
// nothing to auto-login into yet.
export const telegramAppLogin = async (
  initData: string
): Promise<{ linked: boolean }> => {
  const { data, error } = await supabase.functions.invoke("telegram-auth", {
    body: { action: "login", initData },
  });
  if (error) throwUa(error);
  const result = data as { linked: boolean; hashedToken?: string };
  if (result.linked && result.hashedToken) {
    const { error: otpError } = await supabase.auth.verifyOtp({
      token_hash: result.hashedToken,
      type: "magiclink",
    });
    if (otpError) throwUa(otpError);
  }
  return { linked: result.linked };
};

// Links the current Telegram identity (from initData) to the signed-in user.
// Requires an active Supabase session — supabase.functions.invoke attaches it
// automatically as the Authorization header.
export const telegramAppLink = async (initData: string): Promise<void> => {
  const { data, error } = await supabase.functions.invoke("telegram-auth", {
    body: { action: "link", initData },
  });
  if (error) {
    const code = await getFunctionErrorCode(error);
    throw new Error(
      code === "telegram_taken"
        ? "Цей Telegram-акаунт вже підключено до іншого користувача"
        : "Не вдалося підключити Telegram"
    );
  }
  const result = data as { ok?: boolean };
  if (!result.ok) {
    throw new Error("Не вдалося підключити Telegram");
  }
};

export const linkTelegramAppAfterAuth = async (search: string): Promise<void> => {
  if (!isTelegramAppAuth(search)) return;
  const initData = getTelegramAppInitData();
  if (!initData) {
    throw new Error("Не вдалося отримати дані Telegram. Відкрийте застосунок через Telegram.");
  }
  await telegramAppLink(initData);
};
